/**
 * Constants for property names used in analytics events.
 *
 * These constants ensure consistency in property naming across the application.
 *
 * Property-spelling consolidation: where a `*_CANONICAL` snake_case
 * key sits beside its camelCase sibling (and `ENV` beside `ENVIRONMENT`), the
 * canonical spelling is the single source of truth and the sibling is a legacy alias
 * emitted only for the backward-compat parity window. The canonical→alias mapping and
 * the mirror logic live in `property-consolidation.ts`.
 */

import { EXCEPTION_CATALOG_PROPERTIES } from '../../../../../../shared/analytics/exception-catalog.js';

export const COMMON_PROPERTIES = {
  /** Legacy alias of `user_id` during the backward-compat parity window. */
  USER_ID: 'userId',
  USER_ID_CANONICAL: 'user_id',
  LAST_KNOWN_USER_ID: 'last_known_user_id',
  /** Legacy alias of `session_id` during the backward-compat parity window. */
  SESSION_ID: 'sessionId',
  SESSION_ID_CANONICAL: 'session_id',
  /** Legacy alias of `env` during the backward-compat parity window. */
  ENVIRONMENT: 'environment',
  // Canonical environment spelling — canonical-event-map.json requires `env`.
  ENV: 'env',
  VENDOR: 'vendor',
  PLATFORM: 'platform',
  // CPU architecture of the machine (process.arch: 'arm64' | 'x64' | 'ia32' | 'arm').
  // Desktop-only: bridged from Electron main; the browser cannot detect Apple Silicon
  // (Safari and Chromium both report "Intel"), so web sends carry NO arch rather than
  // a present-but-wrong value that would merge the chip cohorts.
  ARCH: 'arch',
  APP_VERSION: 'app_version',
  /**
   * The build an error belongs to, for PostHog's Errors product.
   *
   * Carries the same string as `app_version` but is a separate consumer contract:
   * PostHog's Errors product groups and filters crash reports by `release`, and
   * records the same field against uploaded symbol sets, so the two only line up
   * when both name the build identically. It reads it off the event like any
   * other property. Registered as a posthog-js super property (see
   * posthog/manager.ts) rather than added per-capture, because the SDK's own
   * error tracking emits `$exception` without going through the manager's
   * capture path.
   */
  RELEASE: 'release',
  STUDIO_VERSION: 'studio_version',
  ELECTRON_VERSION: 'electron_version',
  // Keep context-envelope property names shared across renderer call sites.
  CLIENT: 'client',
  PROCESS_TYPE: 'process_type',
  BOOT_ID: 'boot_id',
  LAUNCH_ID: 'launch_id',
  // One id per recorded incident, minted where the problem is first seen and
  // repeated on every line and event about it so they can be joined. Used by the
  // main-process renderer_health_reload record and the ENG-4873 failed-start record.
  INCIDENT_ID: 'incident_id',
  AUTH_JOURNEY_ID: 'auth_journey_id',
  SOURCE: 'source',
  SURFACE: 'surface',
  LINKING_SURFACE: 'linking_surface',
  RESULT: 'result',
  VALUE: 'value',
  COMMAND: 'command',
  BOOT_DURATION_MS: 'boot_duration_ms',
  // ENG-4321 terminal-outcome props. attempt_number is 1-based; elapsed_ms is
  // wall-clock from the first attempt in the window; is_terminal marks the
  // attempt that ends the bounded window. Shared constants (not call-site
  // literals) so app code and the e2e receipt read the SAME property names.
  ATTEMPT_NUMBER: 'attempt_number',
  ELAPSED_MS: 'elapsed_ms',
  IS_TERMINAL: 'is_terminal',
  ERROR_MESSAGE: 'error_message',
  ERROR_TYPE: 'error_type',
  // ENG-4419: last boot stage the renderer reached when the boot-deadline
  // watchdog fired. Additive/optional on boot_failed — most boot_failed emits
  // come from real error paths that never set a stage, so it stays out of
  // boot_failed's required_properties (canonical-event-map.json).
  LAST_BOOT_STAGE: 'last_boot_stage',
  // Launch identity and active/human accounting survive automatic recovery.
  ATTEMPT_ID: 'attempt_id',
  ATTEMPT_GENERATION: 'attempt_generation',
  BOOT_ACTIVE_MS: 'boot_active_ms',
  BOOT_USER_WAIT_MS: 'boot_user_wait_ms',
  // ENG-4419: relaunch-loop signal properties, required on boot_relaunch_loop.
  // relaunch_count = launches observed within the window; window_ms = the
  // detection window the count was measured over.
  RELAUNCH_COUNT: 'relaunch_count',
  WINDOW_MS: 'window_ms',
  COMMAND_KEY: 'command_key',
  MODAL_INSTANCE_ID: 'modal_instance_id',
  ADDED_AT: 'added_at',
  UPTIME_SECONDS: 'uptime_seconds',
  TOOL_USED: 'tool_used',
  QUALITY_SCORE: 'quality_score',
  SUBJECT_SOURCE: 'subject_source',
  REASON: 'reason',
  TIMESTAMP: 'timestamp',
  // The room a studio-scoped event belongs to. Lives here (the shared property-name
  // home) rather than in a feature module so the emit path, the canonical map and
  // the e2e receipt can all name it from one place — this file imports only the
  // dependency-free shared catalog, which is what lets the Electron e2e specs import
  // it directly across packages.
  ROOM_ID: 'room_id',
  // SEVERITY, BLOCKING, ERROR_WHAT and ERROR_WHY: owned by the shared exception
  // catalog, which the Electron main process stamps from too.
  ...EXCEPTION_CATALOG_PROPERTIES
} as const;

/** Shared renderer values prevent envelope discriminators from drifting. */
export const ENVELOPE_PROCESS_TYPE = {
  RENDERER: 'renderer',
  // Encoder-originated errors are attributed to the encoder, not to the bridge
  // that relays them: the Go PostHog forwarder is compiled only into the encoder
  // module's own binaries, so the encoder is the sole producer on this channel.
  // Must stay byte-identical to posthogforwarder.ProcessType in
  // bridge/encoder/pkg/observability/posthogforwarder/forwarder.go — the renderer
  // relay uses this as its fallback for pre-stamp encoders, so a one-sided edit
  // would split one crash source across two process_type values.
  ENCODER: 'encoder'
} as const;

export const ENVELOPE_CLIENT = {
  ELECTRON: 'electron',
  STANDALONE_WEB: 'standalone-web'
} as const;

/**
 * The client surface an event was emitted from. Exported as a type (not just the
 * value map) so feature-gate call sites can pass a surface around without ever
 * widening to `string` — an unknown surface fails type-check at the call site.
 */
export type EnvelopeClient = (typeof ENVELOPE_CLIENT)[keyof typeof ENVELOPE_CLIENT];

/** Existing renderer `platform` values, kept distinct from the client surface. */
export const RENDERER_ANALYTICS_PLATFORM = {
  ELECTRON: 'electron',
  WEB: 'web'
} as const;

/** Existing preload environment keys that share analytics identity with Electron main. */
export const ANALYTICS_ID_ENV_NAMES = {
  BOOT_ID: 'IRIS_BOOT_ID',
  SESSION_ID: 'IRIS_ANALYTICS_SESSION_ID',
  // Electron main mirrors process.arch into this env var so the renderer's envelope
  // resolver can stamp `arch` from the SAME preload bridge that carries boot/session
  // ids. Absent in the web build (no preload), which is exactly why web omits arch.
  ARCH: 'IRIS_ARCH'
} as const;

// Severity vocabulary (S1/fatal..S4/info) is owned by the shared exception catalog so
// renderer events and Electron main exceptions stamp one set of values.
export {
  EVENT_SEVERITY,
  type EventSeverity
} from '../../../../../../shared/analytics/exception-catalog.js';

/** Property names carried by the desktop-only feature-gate guidance signal. */
export const FEATURE_GATE_PROPERTIES = {
  FEATURE: 'feature',
  GATE_REASON: 'gate_reason',
  /**
   * The surface the GATE evaluated, which is a different fact from the envelope's
   * `client`. `client` is owned end-to-end by PostHogManager.getClientProperties(),
   * which re-derives it from the running process and spreads it LAST over every
   * capture — a domain event cannot override it and must not pretend to. This
   * property records the value the gate actually branched on, so the emission always
   * carries its own cause. In the real browser build the two agree by construction
   * (both read `isElectronApp()`).
   */
  GATE_CLIENT: 'gate_client'
} as const;

/**
 * Features that only work in the desktop app. A browser user reaching one of
 * these is EXPECTED — the value identifies which capability the guidance was
 * about, so dashboards can rank what web users are actually trying to do.
 * Typed const (not a call-site literal) per the ENG-4340 typed-name contract.
 *
 * ENG-4332: a value belongs here ONLY if a user gesture can reach its gate. The
 * ranking these values feed is a DEMAND signal, so a capability the app attempts on
 * its own (a connect loop, a reconnect handler, a retry watchdog) must not appear —
 * its count would measure attempts, not people. `local_bridge_connection` was removed
 * for exactly that reason: every caller of LiveKit `startLocal` is automatic
 * machinery, and the browser user's real gesture for that capability is the Local Mode
 * toggle, already covered by LOCAL_MODE. Enforced by
 * analytics/domains/feature-gate.call-sites.test.ts.
 */
export const DESKTOP_ONLY_FEATURE = {
  /** IP-controller (VISCA-over-IP joystick) management — spawns a local process. */
  IP_CONTROLLER_MANAGEMENT: 'ip_controller_management',
  /** Local Mode — routes media over the LAN via the desktop-hosted bridge. */
  LOCAL_MODE: 'local_mode'
} as const;

export type DesktopOnlyFeature = (typeof DESKTOP_ONLY_FEATURE)[keyof typeof DESKTOP_ONLY_FEATURE];

/**
 * Why a feature gate closed. Separate from the feature itself so a future gate
 * class (entitlement, feature flag, hardware) is a new value here rather than a
 * second event name.
 */
export const FEATURE_GATE_REASON = {
  DESKTOP_ONLY: 'desktop_only'
} as const;

export type FeatureGateReason = (typeof FEATURE_GATE_REASON)[keyof typeof FEATURE_GATE_REASON];

export const DEVICE_PROPERTIES = {
  /** Legacy alias of `device_id` during the backward-compat parity window. */
  DEVICE_ID: 'deviceId',
  DEVICE_ID_CANONICAL: 'device_id',
  DEVICE_TYPE: 'deviceType',
  BRAND: 'brand',
  DEVICE_BRAND: 'device_brand',
  DEVICE_BRAND_ID: 'device_brand_id',
  BRAND_NAME: 'brand_name',
  MODEL: 'model',
  DEVICE_MODEL: 'device_model',
  DEVICE_MODEL_ID: 'device_model_id',
  MODEL_NAME: 'model_name',
  TARGET_DEVICE_ID: 'target_device_id',
  TARGET_BRIDGE_ID: 'target_bridge_id',
  SOURCE_DEVICE_ID: 'source_device_id',
  IS_EMBEDDED: 'is_embedded',
  // First-shadow vs reconnect bootstrap on device_shadowed.
  SHADOW_KIND: 'shadow_kind',
  // ENG-3960 §E12 — explicit error-flag cohort prop for media_quality_ping.
  IS_ERROR_STATE: 'is_error_state',
  ERROR_TYPE: 'error_type',
  MEDIA_TYPE: 'media_type',
  PERMISSIONS_MODAL_COUNT: 'permissions_modal_count',
  CONTROL_PROTOCOL: 'controlProtocol',
  SHADOW_SOURCE_AGENT_ID: 'shadow_source_agent_id',
  SHADOW_TARGET_AGENT_ID: 'shadow_target_agent_id',
  BITRATE: 'bitrate',
  RESOLUTION: 'resolution',
  FPS: 'fps'
} as const;

/**
 * discovery_mismatch and discovery_empty properties. PRIMARY_/SECONDARY_
 * (ENG-4322) are the two conflicting protocol/name labels a "Web/NDI
 * mislabel" reconcile gap surfaces. PRE_FILTER_COUNT (ENG-4450) is the
 * device count before dedupe/protocol-presence/feature-flag/tab filtering —
 * without it, discovery_empty is indistinguishable from "the network
 * answered nothing" even though it also fires when a filter tab hid every
 * result. Typed const per the ENG-4340 typed-name contract — call sites read
 * these keys, never hand-write the property strings.
 */
export const DISCOVERY_PROPERTIES = {
  PRIMARY_PROTOCOL: 'primary_protocol',
  SECONDARY_PROTOCOL: 'secondary_protocol',
  PRIMARY_NAME: 'primary_name',
  SECONDARY_NAME: 'secondary_name',
  PRE_FILTER_COUNT: 'pre_filter_count'
} as const;

export const STUDIO_PROPERTIES = {
  /** Legacy alias of `studio_id` during the backward-compat parity window. */
  STUDIO_ID: 'studioId',
  STUDIO_ID_CANONICAL: 'studio_id',
  STUDIO_NAME: 'studioName'
} as const;

export const ORGANIZATION_PROPERTIES = {
  ORG_ID: 'orgId',
  ORG_NAME: 'orgName'
};

export const BRIDGE_PROPERTIES = {
  BRIDGE_TYPE: 'bridgeType' // 'local' | 'secondary'
};

export const COLLABORATION_PROPERTIES = {
  INVITEE_EMAIL: 'inviteeEmail',
  INVITEE_ROLE: 'inviteeRole'
};

export const BILLING_PROPERTIES = {
  PLAN_TYPE: 'planType', // 'free', 'standard', 'pro', 'enterprise', 'custom'
  BILLING_FREQUENCY: 'billingFrequency', // 'monthly', 'yearly'
  INVOICE_ID: 'invoiceId',
  PAYMENT_METHOD_TYPE: 'paymentMethodType'
};

export const CONTROLLER_PROPERTIES = {
  CONTROLLER_ID: 'controllerId',
  CONTROLLER_TYPE: 'controllerType',
  CONTROLLER_NAME: 'controllerName',
  HOTKEY_COMMAND: 'hotkeyCommand',
  HOTKEY_NAME: 'hotkeyName',
  IP_ADDRESS: 'ipAddress',
  PORT: 'port'
};

export const AGENT_PROPERTIES = {
  /** Legacy alias of `agent_id` during the backward-compat parity window. */
  AGENT_ID: 'agentId',
  AGENT_ID_CANONICAL: 'agent_id',
  AGENT_NAME: 'agentName',
  AGENT_VERSION: 'agentVersion',
  AGENT_STATUS: 'agentStatus',
  AGENT_EMBEDDED: 'agentEmbedded'
} as const;

export const GRID_LINES_PROPERTIES = {
  GRID_LINES: 'gridLines'
};

export const MOVEMENT_TOOLS_PROPERTIES = {
  TOOL_NAME: 'toolName',
  PRESET_ID: 'presetId',
  PRESET_NAME: 'presetName',
  PRESET_GROUP_ID: 'presetGroupId',
  PRESET_GROUP_NAME: 'presetGroupName',
  TIME_SPENT: 'timeSpent',
  FOCUS_SPEED: 'focusSpeed',
  JOYSTICK_DIRECTION: 'joystickDirection',
  JOYSTICK_INTENSITY: 'joystickIntensity',
  SHOT_TYPE: 'shotType'
};

export const RECORDING_PROPERTIES = {
  DEVICE_IDS: 'deviceIds',
  RECORDING_PATH: 'recordingPath',
  RECORDING_DURATION: 'recordingDuration'
};

export const SECURITY_PROPERTIES = {
  METHOD: 'method',
  SUCCESS: 'success'
};

export const SHARING_PROPERTIES = {
  PERMISSION_TYPE: 'permissionType',
  EXPIRATION_TIME: 'expirationTime',
  INVITEE_EMAIL: 'inviteeEmail',
  INVITEE_ROLE: 'inviteeRole',
  LINK_TYPE: 'linkType'
};

export const VIDEO_MODES_PROPERTIES = {
  MODE: 'mode',
  PREVIOUS_MODE: 'previousMode',
  IS_LOCKED: 'isLocked',
  SUCCESS: 'success',
  AGENT_IDS: 'agentIds'
};

export const VIDEO_SOURCES_PROPERTIES = {
  SOURCE_TYPE: 'sourceType',
  SOURCE_PROTOCOL: 'sourceProtocol',
  SOURCE_ID: 'sourceId',
  SOURCE_NAME: 'sourceName',
  SOURCE_IP: 'sourceIp',
  SOURCE_PORT: 'sourcePort',
  SOURCE_URL: 'sourceUrl',
  BRAND: 'brand',
  MODEL: 'model',
  CONTROL_TYPE: 'controlType',
  PASSWORD_REQUIRED: 'passwordRequired',
  SETTINGS_CONFIRMED: 'settingsConfirmed',
  SLEEP_MODE: 'sleepMode',
  VIEW_TYPE: 'viewType'
};

export const WORKSPACE_PROPERTIES = {
  ORG_ID: 'orgId',
  ORG_NAME: 'orgName',
  STUDIO_ID: 'studioId',
  STUDIO_NAME: 'studioName',
  WEBSITE: 'website',
  INDUSTRY: 'industry',
  PHOTO_URL: 'photoUrl',
  PREVIOUS_VALUE: 'previousValue'
};
